/**
 * SectionTitle — the heading one level under the page title (`STYLE.md` §8).
 *
 * 14/20 `--font-sans` 600, `--neutral-800`, `margin-bottom: --space-2`. It names a group
 * inside a screen: a card's contents, a settings block, "members by firm", "parties on this
 * project". Above it is `ScreenHead`; below it is `Eyebrow`; between them is nothing, which
 * is the point — the 2026-09-16 review found headings at 15px, 17px and 22px, each invented
 * to sit in a gap the ramp deliberately does not have.
 *
 * **The count is an eyebrow, not part of the sentence.** "Members 12" reads as a heading with
 * a number glued to it; the count is a different kind of fact — the system's answer rather
 * than the section's name — so it is drawn in the eyebrow's tracked capitals beside the
 * title, and it renders as a `span` because a heading cannot contain a heading.
 *
 * **It is not uppercase.** Display capitals belong to names and labels (§1): the page title,
 * the eyebrow, the column head. A section title is read as a phrase, so it is sans and it is
 * set in the case its author wrote it in.
 *
 * Every string is a prop; this component owns no catalog keys.
 */

import type { ReactNode } from "react";

import { Eyebrow } from "./Eyebrow";
import styles from "./Typography.module.css";

export type SectionTitleElement = "h2" | "h3";

export interface SectionTitleProps {
  /**
   * `h2` by default, which is right under a workspace page's `h1`. Pass `h3` inside a project,
   * where `ScreenHead` is already the `h2` — the outline is the caller's to keep true.
   */
  readonly as?: SectionTitleElement;
  /** Optional, and drawn beside the title as an eyebrow. A count, a state, a short qualifier. */
  readonly count?: ReactNode | undefined;
  readonly children: ReactNode;
}

export function SectionTitle({ as: Element = "h2", count, children }: SectionTitleProps) {
  return (
    <Element className={styles.sectionTitle}>
      <span className={styles.sectionTitleText}>{children}</span>
      {count === undefined ? null : <Eyebrow>{count}</Eyebrow>}
    </Element>
  );
}
